import type { ArtifactContent } from "../ChatEventTypes.ts";
import type { QueryContext } from "../components/FocusBanner.tsx";
import type { MentionData } from "../components/Mention.tsx";
import type { MentionedVideoItem } from "../components/VideoMention.tsx";
import { mentionPlaceholderId } from "../utils/parseSpeakingBlockSegments.ts";
import {
	type AnswerState,
	type ChatPanelUiState,
	createInitialAnswerState,
	type ExecutionBlockState,
	type InteractionState,
	type PlanItemState,
	type PlanSectionState,
	type SpeakingBlockState,
	type ThinkingImageTickerItem,
	type ThinkingLogEntry,
} from "./chatPanelState.ts";
import type {
	ChatStreamEvent,
	ChatStreamEventContents,
	ChatStreamEventType,
	ExecutionContent,
	ExecutionImageResultContent,
	VideoMentionContent,
} from "./chatStreamEvents.ts";

/**
 * Artifacts received via `artifact_created`, keyed on `content.id`. `rendered`
 * flips once an artifact has been placed in a speaking block (either by an
 * `artifact_mention` or the end-of-speaking flush of leftovers).
 */
export type ChatStreamArtifacts = Map<
	string,
	{ content: ArtifactContent; rendered: boolean }
>;

export type ChatStreamContext = {
	/** Event types the host app registered for (`registerForChatEvents`). */
	registeredEvents: readonly string[];
	/** Wall-clock time in milliseconds. */
	now: number;
	artifacts: ChatStreamArtifacts;
	/** Social account public ids whose avatars the thinking image ticker never shows. */
	excludedTickerSocialAccountIds?: readonly string[];
};

/** Side effects an event asks for, to be run in order once the state is updated. */
export type ChatStreamEffect =
	/** Relay the stream event to the host app as a `chatevent`. */
	| {
			kind: "dispatch_chat_event";
			detail: { interaction_id: string | null; event: ChatStreamEvent };
	  }
	| { kind: "record_mention"; eventType: string; content: unknown }
	| { kind: "end_interaction" }
	| { kind: "enable_ask" }
	| { kind: "answer_complete" }
	| { kind: "alert"; message: string }
	/** `live` is false while replaying history, when the host isn't told. */
	| {
			kind: "query_context";
			contextId: string;
			queryContext: QueryContext;
			live: boolean;
	  }
	/** Append later through {@link appendExecutionImageResult}. */
	| {
			kind: "queue_execution_image_result";
			result: ExecutionImageResultContent;
			dispatchedToApp: boolean;
	  }
	| { kind: "unknown_event"; eventType: string };

/** The event on one stream line, or undefined when the line isn't one. */
export function parseChatStreamEvent(
	line: string,
): ChatStreamEvent | undefined {
	let event: unknown;
	try {
		event = JSON.parse(line);
	} catch (e) {
		console.log("Failed to decode event: ", e, line);
		return undefined;
	}
	if (typeof event !== "object" || event === null) {
		return undefined;
	}
	return event as ChatStreamEvent;
}

/**
 * Applies one stream event to `s`, mutating it in place (inside Solid's
 * `produce`, or on a plain object), and returns the effects it asks for.
 */
export function reduceChatStreamEvent(
	s: ChatPanelUiState,
	event: ChatStreamEvent,
	context: ChatStreamContext,
): ChatStreamEffect[] {
	const effects: ChatStreamEffect[] = [];

	if (s.renderingHistory) {
		const timestampMs = eventTimestampMs(event.content);
		if (timestampMs !== undefined && context.now - timestampMs < 10_000) {
			s.renderingHistory = false;
		}
	}

	// Host apps only consume live stream events. While replaying history the app does not need to be notified.
	const dispatchedToApp =
		!s.renderingHistory && context.registeredEvents.includes(event.type);
	if (dispatchedToApp) {
		effects.push({
			kind: "dispatch_chat_event",
			detail: { interaction_id: s.latestInteractionId ?? null, event },
		});
	}

	// Ahead of the handlers below, whose mention cases return early once the
	// event has been dispatched to the app: a mention is collected however it
	// arrived, so that `getMentions()` is complete in embedded mode too.
	effects.push({
		kind: "record_mention",
		eventType: event.type,
		content: event.content,
	});

	if (isHandledEventType(event.type)) {
		// The one place the stream's JSON is trusted to match its type.
		const handle = handlers[event.type] as (
			s: ChatPanelUiState,
			content: unknown,
			env: HandlerEnv,
		) => void;
		handle(s, event.content, { context, dispatchedToApp, effects });
	} else {
		effects.push({ kind: "unknown_event", eventType: event.type });
	}

	return effects;
}

/** Starts a new interaction, unless the current one can take `interactionId`. */
export function ensureInteraction(s: ChatPanelUiState, interactionId?: string) {
	let reused = false;
	const current = currentInteraction(s);

	if (interactionId !== undefined && current) {
		if (!current.id) {
			current.id = interactionId;
			reused = true;
		} else if (current.id === interactionId) {
			reused = true;
		}
	}

	if (!reused) {
		s.interactions.push({
			id: interactionId,
			index: s.nextInteractionIndex++,
			ephemeralNotifications: [],
		});
	}

	if (interactionId !== undefined) {
		s.latestInteractionId = interactionId;
	}
}

export function appendQuestion(s: ChatPanelUiState, text: string) {
	const interaction = currentInteraction(s);
	if (interaction && !interaction.question) {
		interaction.question = text;
	}
}

export function appendExecutionImageResult(
	s: ChatPanelUiState,
	result: ExecutionImageResultContent,
	dispatchedToApp = false,
	excludedTickerSocialAccountIds: readonly string[] = [],
) {
	const answer = currentInteraction(s)?.answer;
	if (!answer) {
		return;
	}
	let block = findExecution(answer.executions, result.id);
	if (!block) {
		block = {
			id: result.id,
			segments: [],
			images: [],
			socialAccounts: [],
		};
		answer.executions.push(block);
	}
	block.images.push(result.image_url);
	appendThinkingLogExecutionImage(
		ensureThinkingLogExecutionEntry(answer, result.id),
		result,
	);
	if (!dispatchedToApp) {
		appendExecutionImageTickerItems(
			answer,
			result,
			s.renderingHistory,
			excludedTickerSocialAccountIds,
		);
	}
}

/**
 * Every artifact the panel has rendered, which is also every artifact that
 * can carry a rating: the per-interaction answer transcript plus whatever
 * arrived as `artifact_created`.
 */
export function renderedArtifactIds(
	s: ChatPanelUiState,
	artifacts: ChatStreamArtifacts,
): string[] {
	const ids = new Set<string>(artifacts.keys());
	for (const interaction of s.interactions) {
		if (interaction.artifactId) {
			ids.add(interaction.artifactId);
		}
	}
	return [...ids];
}

type HandlerEnv = {
	context: ChatStreamContext;
	dispatchedToApp: boolean;
	effects: ChatStreamEffect[];
};

type ChatStreamHandlers = {
	[K in ChatStreamEventType]: (
		s: ChatPanelUiState,
		content: ChatStreamEventContents[K],
		env: HandlerEnv,
	) => void;
};

function isHandledEventType(type: string): type is ChatStreamEventType {
	return Object.hasOwn(handlers, type);
}

const handlers: ChatStreamHandlers = {
	start_interaction: (s, content) =>
		ensureInteraction(s, content.interaction_id),
	end_interaction: (_s, _content, { effects }) => {
		effects.push({ kind: "end_interaction" });
	},
	start_answer: (s, content, { context }) => {
		const interaction = currentInteraction(s);
		if (!interaction) {
			return;
		}
		interaction.answer = createInitialAnswerState(
			s.renderingHistory,
			eventTimestampMs(content) ?? context.now,
		);
	},
	end_answer: (_s, _content, { effects }) => {
		effects.push({ kind: "enable_ask" }, { kind: "answer_complete" });
	},
	start_speaking: (s, content) => {
		const idx = getCurrentInteractionIndex(s.interactions);
		const answer = s.interactions[idx]?.answer;
		if (!answer) {
			return;
		}
		answer.answerAvailable = true;
		answer.thinkingEndedAtMs = eventTimestampMs(content);
		answer.reasoningEventsOpen = false;
		answer.reasoningToggleOpen = false;
		for (const plan of answer.plans) {
			plan.expired = true;
		}
		const block: SpeakingBlockState = {
			id: `speaking-${s.latestInteractionId ?? idx}-${answer.responseBlocks.length}`,
			markdown: "",
			mentions: {},
			answerType: content.answer_type,
			complete: false,
		};
		answer.responseBlocks.push({ kind: "speaking", block });
	},
	speaking: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		const token = typeof content === "string" ? content : content.token;
		const block = currentSpeakingBlock(s);
		if (block) {
			block.markdown += token;
		}
	},
	end_speaking: (s, _content, { context }) => {
		const block = currentSpeakingBlock(s);
		if (block) {
			appendUnrenderedArtifacts(s, block, context.artifacts);
			block.complete = true;
		}
	},
	social_account_mention: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		appendMention(s, content);
	},
	influencer_mention: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		appendMention(s, content);
	},
	video_mention: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		appendVideoMention(s, content);
	},
	exception: (s, exception, { dispatchedToApp, effects }) => {
		if (dispatchedToApp) {
			return;
		}
		const interaction = currentInteraction(s);
		if (interaction) {
			interaction.exception = exception;
			return;
		}
		effects.push({
			kind: "alert",
			message: `${exception.title ?? "An error occurred"}\n\n${exception.message}`,
		});
	},
	notification: (s, notification, { context, dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		const interaction = currentInteraction(s);
		if (!interaction) {
			return;
		}
		interaction.ephemeralNotifications.push({
			id: `notif-${context.now}`,
			title: notification.title,
			message: notification.message,
		});
	},
	query_context: (s, queryContext, { dispatchedToApp, effects }) => {
		if (dispatchedToApp) {
			return;
		}
		const contextId = `GVTQC-${s.latestInteractionId ?? "unknown"}-${s.queryContextCounter}`;
		s.queryContextCounter++;
		const interaction = currentInteraction(s);
		if (interaction) {
			interaction.queryContextId = contextId;
			interaction.queryContext = queryContext;
		}
		effects.push({
			kind: "query_context",
			contextId,
			queryContext,
			live: !s.renderingHistory,
		});
	},
	question: (s, question) => {
		appendQuestion(s, question);
	},
	artifact_created: (s, content, { context, dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		recordArtifact(s, content, context.artifacts);
	},
	artifact_mention: (s, content, { context, dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		placeMentionedArtifact(s, content, context.artifacts);
	},
	user_input_requested: (s, content, { context, dispatchedToApp }) => {
		if (dispatchedToApp || content.request?.type !== "effort_upgrade") {
			return;
		}
		const answer = currentInteraction(s)?.answer;
		if (!answer) {
			return;
		}
		answer.effortUpgradeRequest = {
			requestId: content.request_id,
			suggestedEffort: content.request.suggested_effort,
			defaultEffort: content.request.default_effort,
			respondByMs: respondByMs(content, context.now),
			live: !s.renderingHistory,
		};
	},
	user_input_request_resolved: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp || content.resolution?.type !== "effort_upgrade") {
			return;
		}
		const answer = currentInteraction(s)?.answer;
		if (!answer) {
			return;
		}
		answer.effortUpgradeResolution = {
			requestId: content.request_id,
			outcome: content.resolution.outcome,
			resolvedEffort: content.resolution.resolved_effort,
			live: !s.renderingHistory,
		};
	},
	start_plan: () => {},
	plan: (s, planItem, { context, dispatchedToApp }) => {
		const interaction = currentInteraction(s);
		if (!interaction) {
			return;
		}
		const answer = ensureAnswer(interaction, s.renderingHistory, context.now);
		let plan = findPlanSection(answer.plans, planItem.plan_id);
		if (!plan) {
			for (const p of answer.plans) {
				p.expired = true;
			}
			plan = {
				planId: planItem.plan_id,
				items: [],
				expired: false,
			};
			answer.plans.push(plan);
		}
		plan.items.push({
			id: planItem.id,
			description: planItem.description,
			status: "pending",
		});
		const title = nonEmptyTrimmed(planItem.title);
		if (!dispatchedToApp) {
			appendTickerTitle(answer, title, s.renderingHistory);
		}
		appendThinkingLogPlanEntry(answer, {
			id: planItem.id,
			title,
			description: planItem.description,
		});
	},
	end_plan: () => {},
	start_execution: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		setPlanItemStatus(s, content, "executing");
	},
	end_execution: (s, content, { dispatchedToApp }) => {
		if (dispatchedToApp) {
			return;
		}
		setPlanItemStatus(s, content, "complete");
	},
	execution: (s, content, { dispatchedToApp }) => {
		applyExecution(s, content, false, dispatchedToApp);
	},
	replace_execution: (s, content, { dispatchedToApp }) => {
		applyExecution(s, content, true, dispatchedToApp);
	},
	execution_image_result: (
		s,
		result,
		{ context, dispatchedToApp, effects },
	) => {
		if (s.renderingHistory) {
			appendExecutionImageResult(s, result, false);
			return;
		}
		const answer = currentInteraction(s)?.answer;
		const block = answer
			? findExecution(answer.executions, result.id)
			: undefined;
		if (!block || block.images.length === 0) {
			appendExecutionImageResult(
				s,
				result,
				dispatchedToApp,
				context.excludedTickerSocialAccountIds,
			);
			return;
		}
		effects.push({
			kind: "queue_execution_image_result",
			result,
			dispatchedToApp,
		});
	},
	execution_social_account_result: (
		s,
		result,
		{ context, dispatchedToApp },
	) => {
		const answer = currentInteraction(s)?.answer;
		if (!answer) {
			return;
		}
		let execution = findExecution(answer.executions, result.id);
		if (!execution) {
			execution = {
				id: result.id,
				segments: [],
				images: [],
				socialAccounts: [],
			};
			answer.executions.push(execution);
		}
		execution.socialAccounts.push({
			handle: result.handle,
			profile_picture_url: result.profile_picture_url,
			platform: result.platform,
			follower_count: result.follower_count,
		});
		ensureThinkingLogExecutionEntry(answer, result.id).socialAccounts.push({
			handle: result.handle,
			profile_picture_url: result.profile_picture_url,
			platform: result.platform,
			follower_count: result.follower_count,
		});
		if (!dispatchedToApp) {
			appendImageTickerItem(
				answer,
				result.profile_picture_url,
				"circle",
				s.renderingHistory,
				{ social_account_id: result.social_account_id },
				context.excludedTickerSocialAccountIds,
			);
		}
	},
};

export function getCurrentInteractionIndex(
	interactions: InteractionState[],
): number {
	return Math.max(0, interactions.length - 1);
}

function currentInteraction(s: ChatPanelUiState): InteractionState | undefined {
	return s.interactions[getCurrentInteractionIndex(s.interactions)];
}

function currentSpeakingBlock(
	s: ChatPanelUiState,
): SpeakingBlockState | undefined {
	const last = currentInteraction(s)?.answer?.responseBlocks.at(-1);
	if (last?.kind === "speaking" && !last.block.complete) {
		return last.block;
	}
	return undefined;
}

/** Read off every event before it is handled, so `content` is not yet typed. */
function eventTimestampMs(content: unknown): number | undefined {
	if (
		typeof content !== "object" ||
		content === null ||
		!("timestamp_nanoseconds" in content)
	) {
		return undefined;
	}
	const timestampNanoseconds = Number(content.timestamp_nanoseconds);
	if (Number.isFinite(timestampNanoseconds)) {
		return timestampNanoseconds / 1_000_000;
	}
	return undefined;
}

/**
 * `respond_by` is on the server's clock, so it is counted from when the event
 * was sent rather than compared with this browser's clock, which can be out
 * by more than the few seconds the user is given.
 */
function respondByMs(
	content: ChatStreamEventContents["user_input_requested"],
	now: number,
): number {
	const respondBy = Date.parse(content.respond_by);
	const sentAt = eventTimestampMs(content);
	if (sentAt === undefined) {
		return respondBy;
	}
	return now + (respondBy - sentAt);
}

function nonEmptyTrimmed(value: string | undefined): string | undefined {
	const trimmed = value?.trim();
	return trimmed ? trimmed : undefined;
}

function appendThinkingLogPlanEntry(
	answer: AnswerState,
	entry: {
		id: string;
		title?: string;
		description?: string;
	},
) {
	const title = entry.title?.trim() ?? "";
	const description = entry.description?.trim() ?? "";
	const existing = answer.thinkingLog.find(
		(item): item is Extract<ThinkingLogEntry, { kind: "plan" }> =>
			item.id === entry.id && item.kind === "plan",
	);

	if (existing) {
		if (title) {
			existing.title = title;
		}
		if (description) {
			existing.description = description;
		}
		return;
	}

	if (!title && !description) {
		return;
	}

	answer.thinkingLog.push({
		id: entry.id,
		kind: "plan",
		title,
		description,
	});
}

function ensureThinkingLogExecutionEntry(
	answer: AnswerState,
	executionId: string,
): Extract<ThinkingLogEntry, { kind: "execution" }> {
	const existing = answer.thinkingLog.find(
		(item): item is Extract<ThinkingLogEntry, { kind: "execution" }> =>
			item.kind === "execution" && item.id === executionId,
	);

	if (existing) {
		return existing;
	}

	const logEntry: Extract<ThinkingLogEntry, { kind: "execution" }> = {
		id: executionId,
		kind: "execution",
		title: "",
		description: "",
		images: [],
		socialAccounts: [],
	};
	answer.thinkingLog.push(logEntry);
	return logEntry;
}

function appendThinkingLogExecutionEntry(
	answer: AnswerState,
	executionId: string,
	title?: string,
	description?: string,
) {
	const entry = ensureThinkingLogExecutionEntry(answer, executionId);
	const trimmedTitle = title?.trim();
	const trimmedDescription = description?.trim();

	if (trimmedTitle) {
		entry.title = trimmedTitle;
	}
	if (trimmedDescription) {
		entry.description = trimmedDescription;
	}
}

function appendThinkingLogExecutionImage(
	entry: Extract<ThinkingLogEntry, { kind: "execution" }>,
	result: ExecutionImageResultContent,
) {
	// A step can surface several frames of the same clip; the log shows one per
	// clip, like the ticker. An image with no video_id can't be matched, so it
	// always appends.
	const videoId = nonEmptyTrimmed(result.video_id);
	if (videoId && entry.images.some((image) => image.video_id === videoId)) {
		return;
	}
	entry.images.push({
		url: result.image_url,
		...(videoId ? { video_id: videoId } : {}),
	});
}

function appendTickerTitle(
	answer: AnswerState,
	title: string | undefined,
	renderingHistory: boolean,
) {
	if (renderingHistory || !title) {
		return;
	}
	if (answer.tickerTitles.at(-1) === title) {
		return;
	}
	answer.tickerTitles.push(title);
}

type ExecutionImageTickerIdentity = {
	video_id?: string;
	social_account_id?: string;
};

function appendImageTickerItem(
	answer: AnswerState,
	url: string | undefined,
	shape: ThinkingImageTickerItem["shape"],
	renderingHistory: boolean,
	identity?: ExecutionImageTickerIdentity,
	excludedSocialAccountIds: readonly string[] = [],
) {
	if (renderingHistory || !url) {
		return;
	}
	const trimmed = url.trim();
	if (!trimmed) {
		return;
	}
	const videoId = nonEmptyTrimmed(identity?.video_id);
	const socialAccountId = nonEmptyTrimmed(identity?.social_account_id);
	if (socialAccountId && excludedSocialAccountIds.includes(socialAccountId)) {
		return;
	}
	if (
		(videoId &&
			answer.tickerImages.some((item) => item.video_id === videoId)) ||
		(socialAccountId &&
			answer.tickerImages.some(
				(item) => item.social_account_id === socialAccountId,
			))
	) {
		return;
	}
	answer.tickerImages.push({
		id: `ticker-image-${answer.tickerImages.length}`,
		url: trimmed,
		shape,
		...(videoId ? { video_id: videoId } : {}),
		...(socialAccountId ? { social_account_id: socialAccountId } : {}),
	});
}

function appendExecutionImageTickerItems(
	answer: AnswerState,
	content: ExecutionImageResultContent,
	renderingHistory: boolean,
	excludedSocialAccountIds: readonly string[],
) {
	appendImageTickerItem(
		answer,
		content.image_url,
		"rounded",
		renderingHistory,
		{ video_id: content.video_id },
	);
	appendImageTickerItem(
		answer,
		content.social_account?.profile_picture_url,
		"circle",
		renderingHistory,
		{ social_account_id: content.social_account?.social_account_id },
		excludedSocialAccountIds,
	);
}

function ensureAnswer(
	interaction: InteractionState,
	renderingHistory: boolean,
	now: number,
): AnswerState {
	if (!interaction.answer) {
		interaction.answer = createInitialAnswerState(renderingHistory, now);
	}
	return interaction.answer;
}

function findPlanSection(
	plans: PlanSectionState[],
	planId: string,
): PlanSectionState | undefined {
	return plans.find((p) => p.planId === planId);
}

function findExecution(
	executions: ExecutionBlockState[],
	id: string,
): ExecutionBlockState | undefined {
	return executions.find((e) => e.id === id);
}

function findPlanItem(
	plans: PlanSectionState[],
	itemId: string,
): PlanItemState | undefined {
	for (const plan of plans) {
		const item = plan.items.find((i) => i.id === itemId);
		if (item) {
			return item;
		}
	}
	return undefined;
}

function setPlanItemStatus(
	s: ChatPanelUiState,
	execution: { id: string },
	status: PlanItemState["status"],
) {
	const answer = currentInteraction(s)?.answer;
	if (!answer) {
		return;
	}
	const item = findPlanItem(answer.plans, execution.id);
	if (item) {
		item.status = status;
	}
}

function appendMention(s: ChatPanelUiState, mention: MentionData) {
	const placeholderId = mentionPlaceholderId(
		s.latestInteractionId ?? "unknown",
		s.mentionCounter++,
	);
	const block = currentSpeakingBlock(s);
	if (!block) {
		return;
	}
	block.markdown += placeholderId;
	block.mentions[placeholderId] = { kind: "mention", data: mention };
}

function appendVideoMention(s: ChatPanelUiState, mention: VideoMentionContent) {
	const placeholderId = mentionPlaceholderId(
		s.latestInteractionId ?? "unknown",
		s.mentionCounter++,
	);
	const index = s.videoMentionCounter++;
	const block = currentSpeakingBlock(s);
	if (!block) {
		return;
	}
	const videoMention: MentionedVideoItem = {
		id: mention.id,
		index,
		start_time_sec: mention.start_time_sec ?? null,
		end_time_sec: mention.end_time_sec ?? null,
		thumbnail_url: mention.thumbnail_url,
		aspect_ratio: mention.aspect_ratio,
		title: mention.title,
		video_id: mention.video_id,
		video_url: mention.video_url,
		video_duration: mention.video_duration ?? null,
		hls_ready: mention.hls_ready,
		influencer: mention.influencer,
		social_account: mention.social_account,
		view_count: mention.view_count,
		post_url: mention.post_url,
	};
	block.markdown += placeholderId;
	block.mentions[placeholderId] = { kind: "video", data: videoMention };
}

function recordArtifact(
	s: ChatPanelUiState,
	content: ArtifactContent,
	artifacts: ChatStreamArtifacts,
) {
	const id = content?.id;
	if (typeof id !== "string" || id.length === 0) {
		return;
	}
	// The interaction transcript is saved from the speaking toolbar; it is
	// never mentioned inline. Other artifact types wait for `artifact_mention`.
	if (content.type === "interaction") {
		const interaction = currentInteraction(s);
		if (interaction) {
			interaction.artifactId = id;
			const documentId = content.document_id;
			if (typeof documentId === "string" && documentId.length > 0) {
				interaction.savedDocumentPublicId = documentId;
			}
			if (typeof content.is_favourite === "boolean") {
				interaction.savedDocumentIsFavourite = content.is_favourite;
			}
		}
		return;
	}
	const existing = artifacts.get(id);
	if (existing) {
		if (
			typeof content.document_id === "string" &&
			content.document_id.length > 0
		) {
			existing.content.document_id = content.document_id;
		}
		if (typeof content.is_favourite === "boolean") {
			existing.content.is_favourite = content.is_favourite;
		}
		return;
	}
	artifacts.set(id, { content, rendered: false });
}

/**
 * Append an artifact entry (+ placeholder) to a speaking block. Artifacts
 * render as their own block, so the placeholder is isolated on its own line;
 * this also keeps adjacent placeholder ids from concatenating (which would
 * break the id-based substitution done when the block is rendered).
 */
function placeArtifactInBlock(
	s: ChatPanelUiState,
	block: SpeakingBlockState,
	artifact: ArtifactContent,
) {
	const placeholderId = mentionPlaceholderId(
		s.latestInteractionId ?? "unknown",
		s.mentionCounter++,
	);
	if (block.markdown.length > 0 && !block.markdown.endsWith("\n")) {
		block.markdown += "\n\n";
	}
	block.markdown += placeholderId;
	block.mentions[placeholderId] = { kind: "artifact", data: artifact };
}

function placeMentionedArtifact(
	s: ChatPanelUiState,
	mention: { id?: string; type?: string },
	artifacts: ChatStreamArtifacts,
) {
	const id = typeof mention?.id === "string" ? mention.id : undefined;
	const stored = id ? artifacts.get(id) : undefined;
	// A missing artifact (never received, or referenced without an id) still
	// renders — as a pill built from whatever type the mention carried.
	const artifact: ArtifactContent = stored?.content ?? {
		id: id ?? "",
		type: mention?.type ?? "unknown",
	};
	if (stored) {
		stored.rendered = true;
	}
	const block = currentSpeakingBlock(s);
	if (!block) {
		return;
	}
	placeArtifactInBlock(s, block, artifact);
}

/** Append any created-but-not-yet-mentioned artifacts to the block. */
function appendUnrenderedArtifacts(
	s: ChatPanelUiState,
	block: SpeakingBlockState,
	artifacts: ChatStreamArtifacts,
) {
	for (const stored of artifacts.values()) {
		if (stored.rendered || stored.content.type === "interaction") {
			continue;
		}
		placeArtifactInBlock(s, block, stored.content);
		stored.rendered = true;
	}
}

function applyExecution(
	s: ChatPanelUiState,
	execution: ExecutionContent,
	replace: boolean,
	dispatchedToApp: boolean,
) {
	const answer = currentInteraction(s)?.answer;
	if (!answer) {
		return;
	}
	const id = execution.id;
	let block = findExecution(answer.executions, id);
	if (!block) {
		block = { id, segments: [], images: [], socialAccounts: [] };
		answer.executions.push(block);
	}
	if (!replace) {
		block.segments.push({});
	} else if (block.segments.length === 0) {
		block.segments.push({});
	}
	const segment = block.segments[block.segments.length - 1];
	if (!s.renderingHistory) {
		const progress = execution.progress;
		if (progress.type === "continuous") {
			segment.progress = {
				type: "continuous",
				percentage: progress.percentage,
			};
			// Most steps only ever report a single 1.0 as they finish, which
			// would flash a full bar, so only start tracking an execution that
			// reports genuine sub-100% progress. Once tracked it stays on view
			// at 100%: deck generation has minutes of silent work left after
			// its last slide, and removing the bar there reads as abandoned.
			const tracked = answer.liveProgress?.executionId === id;
			if (progress.percentage < 1 || tracked) {
				answer.liveProgress = {
					executionId: id,
					description: nonEmptyTrimmed(execution.description),
					percentage: progress.percentage,
				};
			}
		}
	}
	const title = nonEmptyTrimmed(execution.title);
	if (!dispatchedToApp) {
		appendTickerTitle(answer, title, s.renderingHistory);
	}
	appendThinkingLogExecutionEntry(
		answer,
		id,
		title,
		nonEmptyTrimmed(execution.description),
	);
}
